<script>
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faBell, faFileLines } from '@fortawesome/pro-light-svg-icons';

  export let events = [];
  export let timezone = 'UTC';
  export let onEventClick = () => {};
  export let onEventHover = () => {};
  export let onEventLeave = () => {};

  const MARKER_SIZE_PX = 24;
  const LANE_GAP = 4;
  const LANE_COUNT = 4;
  const TIMELINE_PADDING_TOP = 6;
  const AXIS_MARGIN_ABOVE = 8;

  $: markerAreaHeight =
    TIMELINE_PADDING_TOP + LANE_COUNT * MARKER_SIZE_PX + (LANE_COUNT - 1) * LANE_GAP;
  $: axisTop = markerAreaHeight + AXIS_MARGIN_ABOVE;
  $: tickTop = axisTop - 8;
  $: labelTop = axisTop + 6;
  $: totalHeight = axisTop + 24;

  let hoveredEventId = null;
  let hoveredRect = null;
  let timelineEl = null;

  function getMinutesIntoDay(timestamp, eventTimezone = timezone) {
    const localDate = new Date(
      new Date(timestamp).toLocaleString('en-US', {
        timeZone: eventTimezone,
      })
    );
    return localDate.getHours() * 60 + localDate.getMinutes();
  }

  function getEventPosition(timestamp, eventTimezone = timezone) {
    return (getMinutesIntoDay(timestamp, eventTimezone) / (24 * 60)) * 100;
  }

  function getLaneTop(index) {
    const lane = index % LANE_COUNT;
    return TIMELINE_PADDING_TOP + lane * (MARKER_SIZE_PX + LANE_GAP);
  }

  function getConnectorHeight(index) {
    return Math.max(0, axisTop - getLaneTop(index) - MARKER_SIZE_PX);
  }

  function formatEventTime(timestamp, eventTimezone = timezone) {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      timeZone: eventTimezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  function getEventIcon(kind) {
    return kind === 'form' ? faFileLines : faBell;
  }

  function getEventTone(kind) {
    return kind === 'form'
      ? 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-500/20 dark:text-sky-200 dark:ring-sky-400/30'
      : 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-500/20 dark:text-amber-100 dark:ring-amber-400/30';
  }

  function handleHover(event, e) {
    hoveredEventId = event.id;
    const btn = e.currentTarget;
    if (btn && timelineEl) {
      const btnRect = btn.getBoundingClientRect();
      const containerRect = timelineEl.getBoundingClientRect();
      hoveredRect = {
        left: btnRect.left - containerRect.left + btnRect.width / 2,
        top: btnRect.bottom - containerRect.top + 6,
      };
    }
    onEventHover(event);
  }

  function handleLeave() {
    hoveredEventId = null;
    hoveredRect = null;
    onEventLeave();
  }
</script>

<div
  bind:this={timelineEl}
  class="relative border-b ml-1 mr-1 border-stone-200 bg-stone-50/50 pl-1 pr-14 dark:border-gray-700 dark:bg-gray-800/50 print:hidden"
  style={`min-height: ${totalHeight + 40}px;`}
>
  <div class="py-2 text-sm px-2 font-medium text-gray-600 dark:text-gray-400">
    Event timeline
  </div>
  {#if events.length === 0}
    <div class="px-4 py-3 text-center text-xs text-gray-400 dark:text-gray-500">
      No events for this day.
    </div>
  {:else}
    <div class="overflow-x-auto">
      <div class="relative min-w-[960px]" style={`height: ${totalHeight}px;`}>
        <div class="absolute inset-y-0 left-0 right-0">
          <div
            class="absolute left-0 right-0 h-px bg-stone-300 dark:bg-gray-600"
            style={`top: ${axisTop}px;`}
          ></div>

          {#each Array(25) as _, hour}
            <div
              class="absolute h-3 w-px bg-stone-200 dark:bg-gray-700"
              style={`left: ${(hour / 24) * 100}%; top: ${tickTop}px;`}
            ></div>

            {#if hour < 24}
              <div
                class="absolute -translate-x-1/2 text-[9px] text-gray-400 dark:text-gray-500"
                style={`left: ${(hour / 24) * 100}%; top: ${labelTop}px;`}
              >
                {String(hour).padStart(2, '0')}:00
              </div>
            {/if}
          {/each}

          {#each events as event, index (event.id)}
            <button
              type="button"
              class="absolute z-20 -translate-x-1/2"
              style={`left: ${getEventPosition(event.timestamp, event.timezone || timezone)}%; top: ${getLaneTop(index)}px;`}
              on:click={() => onEventClick(event)}
              on:mouseenter={(e) => handleHover(event, e)}
              on:mouseleave={handleLeave}
              on:focus={(e) => handleHover(event, e)}
              on:blur={handleLeave}
            >
              <span
                class="absolute left-1/2 z-10 w-px -translate-x-1/2 bg-stone-300 dark:bg-gray-600"
                style={`top: ${MARKER_SIZE_PX}px; height: ${getConnectorHeight(index)}px;`}
              ></span>

              <span
                class={`relative z-20 flex items-center justify-center rounded-full ring-1 shadow-sm transition-transform hover:scale-110 ${
                  hoveredEventId === event.id ? 'scale-110' : ''
                } ${getEventTone(event.kind)}`}
                style={`width: ${MARKER_SIZE_PX}px; height: ${MARKER_SIZE_PX}px;`}
              >
                <FontAwesomeIcon icon={getEventIcon(event.kind)} class="text-[10px]" />
              </span>
            </button>
          {/each}
        </div>
      </div>
    </div>
  {/if}

  {#if hoveredEventId && hoveredRect}
    {@const hoveredEvent = events.find((e) => e.id === hoveredEventId)}
    {#if hoveredEvent}
      <div
        class="pointer-events-none absolute z-50 -translate-x-1/2 rounded-md bg-gray-100 px-2.5 py-1.5 text-[11px] font-medium leading-tight text-gray-900 shadow-lg dark:bg-gray-100 dark:text-gray-900"
        style={`left: ${hoveredRect.left}px; top: ${hoveredRect.top}px;`}
      >
        {formatEventTime(hoveredEvent.timestamp, hoveredEvent.timezone || timezone)} · {hoveredEvent.title}
      </div>
    {/if}
  {/if}
</div>
